import { useState, useEffect } from "react";
import { AppNavigation } from "../components/AppNavigation";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Eye,
  Plus, 
  Trash2,
  Search,
  TrendingUp,
  FileText,
  Target,
  MessageSquare,
  Bell,
  Sparkles,
  Loader2,
  AlertTriangle,
  Twitter,
  Instagram,
  Facebook,
  Linkedin,
  Youtube,
  ExternalLink,
  Copy,
  Check,
  Lightbulb
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CompetitorAdvisorPage = ({ user, logout, token }) => {
  const [competitors, setCompetitors] = useState([]);
  const [insights, setInsights] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [analyzeDialogOpen, setAnalyzeDialogOpen] = useState(false);
  const [selectedCompetitor, setSelectedCompetitor] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const [newCompetitor, setNewCompetitor] = useState({
    name: "",
    platform: "instagram",
    handle: "",
    profile_url: "",
    notes: ""
  });

  const [analysisRequest, setAnalysisRequest] = useState({
    competitor_id: "",
    content_sample: "",
    analyze_type: "trends"
  });

  const platformIcons = {
    twitter: <Twitter className="w-4 h-4" />,
    instagram: <Instagram className="w-4 h-4" />,
    facebook: <Facebook className="w-4 h-4" />,
    linkedin: <Linkedin className="w-4 h-4" />,
    youtube: <Youtube className="w-4 h-4" />,
    tiktok: <span className="text-xs font-bold">TT</span>
  };

  const platformColors = {
    twitter: "bg-blue-500",
    instagram: "bg-pink-500",
    facebook: "bg-indigo-500",
    linkedin: "bg-sky-500",
    youtube: "bg-red-500",
    tiktok: "bg-gray-800"
  };

  const analyzeTypes = [
    { id: "trends", name: "Tendencias", icon: <TrendingUp className="w-4 h-4" />, description: "Analiza tendencias actuales del sector" },
    { id: "content", name: "Contenido", icon: <FileText className="w-4 h-4" />, description: "Analiza un contenido específico" },
    { id: "strategy", name: "Estrategia", icon: <Target className="w-4 h-4" />, description: "Analiza la estrategia general" },
    { id: "engagement", name: "Engagement", icon: <MessageSquare className="w-4 h-4" />, description: "Analiza tácticas de interacción" }
  ];

  const priorityColors = {
    high: "bg-red-100 text-red-700 border-red-200",
    medium: "bg-yellow-100 text-yellow-700 border-yellow-200",
    low: "bg-green-100 text-green-700 border-green-200"
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [competitorsRes, insightsRes, alertsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/competitors`, { headers }),
        axios.get(`${BACKEND_URL}/api/competitors/insights?limit=10`, { headers }),
        axios.get(`${BACKEND_URL}/api/competitors/alerts`, { headers })
      ]);
      
      setCompetitors(competitorsRes.data);
      setInsights(insightsRes.data);
      setAlerts(alertsRes.data.alerts || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCompetitor = async () => {
    if (!newCompetitor.name.trim() || !newCompetitor.handle.trim()) {
      toast.error("Nombre y handle son requeridos");
      return;
    }

    try {
      await axios.post(
        `${BACKEND_URL}/api/competitors`,
        newCompetitor,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Competidor añadido");
      setAddDialogOpen(false);
      setNewCompetitor({ name: "", platform: "instagram", handle: "", profile_url: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error("Error al añadir competidor");
    }
  };

  const handleDeleteCompetitor = async (competitorId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/competitors/${competitorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Competidor eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleAnalyze = async () => {
    setAnalyzing(true);
    setAnalysisResult(null);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/competitors/analyze`,
        analysisRequest,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAnalysisResult(response.data);
      toast.success("Análisis completado");
      fetchData(); // Refresh insights
    } catch (error) {
      toast.error("Error en el análisis");
      console.error(error);
    } finally {
      setAnalyzing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Copiado al portapapeles");
  };

  return (
    <div className="min-h-screen bg-background">
      <AppNavigation user={user} logout={logout} />
      
      <main className="lg:ml-64 pt-14 lg:pt-0 pb-20 lg:pb-0 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
                <Eye className="w-8 h-8 text-primary" />
                Asesor de Competencia
              </h1>
              <p className="text-muted-foreground mt-1">
                Analiza a tu competencia y descubre oportunidades con IA
              </p>
            </div>
            <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
              <DialogTrigger asChild>
                <Button className="gradient-neural glow-neural text-white" data-testid="add-competitor-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Añadir Competidor
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Añadir Competidor</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Nombre</Label>
                    <Input 
                      placeholder="Nombre del competidor o marca"
                      value={newCompetitor.name}
                      onChange={(e) => setNewCompetitor({ ...newCompetitor, name: e.target.value })}
                      data-testid="competitor-name-input"
                    />
                  </div>
                  <div>
                    <Label>Plataforma Principal</Label>
                    <Select 
                      value={newCompetitor.platform}
                      onValueChange={(v) => setNewCompetitor({ ...newCompetitor, platform: v })}
                    >
                      <SelectTrigger data-testid="competitor-platform-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="instagram">Instagram</SelectItem>
                        <SelectItem value="twitter">Twitter/X</SelectItem>
                        <SelectItem value="facebook">Facebook</SelectItem>
                        <SelectItem value="linkedin">LinkedIn</SelectItem>
                        <SelectItem value="tiktok">TikTok</SelectItem>
                        <SelectItem value="youtube">YouTube</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Handle / Usuario</Label>
                    <Input 
                      placeholder="@username"
                      value={newCompetitor.handle}
                      onChange={(e) => setNewCompetitor({ ...newCompetitor, handle: e.target.value })}
                      data-testid="competitor-handle-input"
                    />
                  </div>
                  <div>
                    <Label>URL del Perfil (opcional)</Label>
                    <Input 
                      placeholder="https://instagram.com/username"
                      value={newCompetitor.profile_url}
                      onChange={(e) => setNewCompetitor({ ...newCompetitor, profile_url: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Notas (opcional)</Label>
                    <Textarea 
                      placeholder="Notas sobre este competidor..."
                      value={newCompetitor.notes}
                      onChange={(e) => setNewCompetitor({ ...newCompetitor, notes: e.target.value })}
                    />
                  </div>
                  <Button className="w-full" onClick={handleAddCompetitor} data-testid="save-competitor-btn">
                    Añadir Competidor
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <Tabs defaultValue="alerts" className="space-y-6">
              <TabsList className="grid grid-cols-4 w-full max-w-lg">
                <TabsTrigger value="alerts" data-testid="tab-alerts">
                  <Bell className="w-4 h-4 mr-2" />
                  Alertas
                </TabsTrigger>
                <TabsTrigger value="competitors" data-testid="tab-competitors">
                  <Eye className="w-4 h-4 mr-2" />
                  Competidores
                </TabsTrigger>
                <TabsTrigger value="analyze" data-testid="tab-analyze">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Analizar
                </TabsTrigger>
                <TabsTrigger value="insights" data-testid="tab-insights">
                  <Lightbulb className="w-4 h-4 mr-2" />
                  Insights
                </TabsTrigger>
              </TabsList>

              {/* Alerts Tab */}
              <TabsContent value="alerts" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Bell className="w-5 h-5 text-accent" />
                      Alertas de Competencia
                    </CardTitle>
                    <CardDescription>
                      Notificaciones sobre actividad relevante de tus competidores
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {alerts.length === 0 ? (
                      <div className="text-center py-8">
                        <AlertTriangle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground mb-4">
                          {competitors.length === 0 
                            ? "Añade competidores para recibir alertas" 
                            : "No hay alertas nuevas"}
                        </p>
                        {competitors.length === 0 && (
                          <Button variant="outline" onClick={() => setAddDialogOpen(true)}>
                            <Plus className="w-4 h-4 mr-2" />
                            Añadir Competidor
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {alerts.map((alert) => (
                          <div 
                            key={alert.alert_id}
                            className={`p-4 border rounded-lg ${priorityColors[alert.priority]}`}
                            data-testid={`alert-${alert.alert_id}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3">
                                <span className="text-2xl">{alert.icon}</span>
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <h4 className="font-semibold">{alert.title}</h4>
                                    <Badge variant="outline" className="text-xs">
                                      {alert.priority === 'high' ? 'Alta' : alert.priority === 'medium' ? 'Media' : 'Baja'}
                                    </Badge>
                                  </div>
                                  <p className="text-sm mb-2">{alert.description}</p>
                                  <div className="flex items-center gap-2 text-sm">
                                    <span className={`p-1 rounded text-white ${platformColors[alert.platform]}`}>
                                      {platformIcons[alert.platform]}
                                    </span>
                                    <span className="font-medium">{alert.competitor_name}</span>
                                    <span className="text-muted-foreground">{alert.competitor_handle}</span>
                                  </div>
                                </div>
                              </div>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => {
                                  setAnalysisRequest({
                                    competitor_id: competitors.find(c => c.name === alert.competitor_name)?.competitor_id || "",
                                    content_sample: "",
                                    analyze_type: "content"
                                  });
                                  setAnalyzeDialogOpen(true);
                                }}
                              >
                                Analizar
                              </Button>
                            </div>
                            <p className="text-xs mt-3 text-muted-foreground">
                              💡 {alert.recommendation}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Competitors Tab */}
              <TabsContent value="competitors" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Tus Competidores</CardTitle>
                    <CardDescription>
                      Gestiona los competidores que quieres monitorizar
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {competitors.length === 0 ? (
                      <div className="text-center py-8">
                        <Eye className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground mb-4">
                          No tienes competidores añadidos
                        </p>
                        <Button onClick={() => setAddDialogOpen(true)}>
                          <Plus className="w-4 h-4 mr-2" />
                          Añadir Competidor
                        </Button>
                      </div>
                    ) : (
                      <div className="grid md:grid-cols-2 gap-4">
                        {competitors.map((comp) => (
                          <div 
                            key={comp.competitor_id}
                            className="p-4 border border-border rounded-lg hover:shadow-sm transition-shadow"
                            data-testid={`competitor-${comp.competitor_id}`}
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <span className={`p-2 rounded text-white ${platformColors[comp.platform]}`}>
                                  {platformIcons[comp.platform]}
                                </span>
                                <div>
                                  <h4 className="font-semibold">{comp.name}</h4>
                                  <p className="text-sm text-muted-foreground">{comp.handle}</p>
                                </div>
                              </div>
                              <div className="flex gap-1">
                                {comp.profile_url && (
                                  <a href={comp.profile_url} target="_blank" rel="noopener noreferrer">
                                    <Button variant="ghost" size="icon">
                                      <ExternalLink className="w-4 h-4" />
                                    </Button>
                                  </a>
                                )}
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => handleDeleteCompetitor(comp.competitor_id)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                            {comp.notes && (
                              <p className="text-sm text-muted-foreground mb-3">{comp.notes}</p>
                            )}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="w-full"
                              onClick={() => {
                                setAnalysisRequest({
                                  competitor_id: comp.competitor_id,
                                  content_sample: "",
                                  analyze_type: "strategy"
                                });
                                setAnalyzeDialogOpen(true);
                              }}
                            >
                              <Sparkles className="w-4 h-4 mr-2" />
                              Analizar con IA
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Analyze Tab */}
              <TabsContent value="analyze" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-accent" />
                      Análisis con IA
                    </CardTitle>
                    <CardDescription>
                      Analiza contenido de la competencia para obtener insights accionables
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Competidor (opcional)</Label>
                        <Select 
                          value={analysisRequest.competitor_id}
                          onValueChange={(v) => setAnalysisRequest({ ...analysisRequest, competitor_id: v })}
                        >
                          <SelectTrigger data-testid="analyze-competitor-select">
                            <SelectValue placeholder="Seleccionar competidor" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="">Ninguno específico</SelectItem>
                            {competitors.map((comp) => (
                              <SelectItem key={comp.competitor_id} value={comp.competitor_id}>
                                {comp.name} ({comp.handle})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Tipo de Análisis</Label>
                        <Select 
                          value={analysisRequest.analyze_type}
                          onValueChange={(v) => setAnalysisRequest({ ...analysisRequest, analyze_type: v })}
                        >
                          <SelectTrigger data-testid="analyze-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {analyzeTypes.map((type) => (
                              <SelectItem key={type.id} value={type.id}>
                                <div className="flex items-center gap-2">
                                  {type.icon}
                                  {type.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label>Contenido a Analizar (opcional)</Label>
                      <Textarea 
                        placeholder="Pega aquí el contenido de un post de la competencia que quieras analizar..."
                        value={analysisRequest.content_sample}
                        onChange={(e) => setAnalysisRequest({ ...analysisRequest, content_sample: e.target.value })}
                        className="min-h-[120px]"
                        data-testid="content-sample-textarea"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Puedes analizar tendencias generales o un contenido específico
                      </p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {analyzeTypes.map((type) => (
                        <div 
                          key={type.id}
                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                            analysisRequest.analyze_type === type.id 
                              ? 'border-primary bg-primary/5' 
                              : 'border-border hover:border-primary/50'
                          }`}
                          onClick={() => setAnalysisRequest({ ...analysisRequest, analyze_type: type.id })}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            {type.icon}
                            <span className="font-medium text-sm">{type.name}</span>
                          </div>
                          <p className="text-xs text-muted-foreground">{type.description}</p>
                        </div>
                      ))}
                    </div>

                    <Button 
                      className="w-full bg-accent hover:bg-accent/90" 
                      onClick={handleAnalyze}
                      disabled={analyzing}
                      data-testid="run-analysis-btn"
                    >
                      {analyzing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Analizando...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Ejecutar Análisis
                        </>
                      )}
                    </Button>

                    {/* Analysis Result */}
                    {analysisResult && (
                      <div className="p-4 bg-secondary/50 rounded-lg border border-border" data-testid="analysis-result">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-accent" />
                            Resultado del Análisis
                          </h4>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => copyToClipboard(analysisResult.analysis)}
                          >
                            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </Button>
                        </div>
                        <div className="prose prose-sm max-w-none">
                          <pre className="whitespace-pre-wrap text-sm bg-background p-4 rounded-lg overflow-auto max-h-96">
                            {analysisResult.analysis}
                          </pre>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Insights Tab */}
              <TabsContent value="insights" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-chart-3" />
                      Historial de Insights
                    </CardTitle>
                    <CardDescription>
                      Análisis anteriores guardados
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {insights.length === 0 ? (
                      <div className="text-center py-8">
                        <Lightbulb className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground mb-4">
                          No tienes insights guardados aún
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Los análisis que realices se guardarán aquí
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {insights.map((insight) => (
                          <div 
                            key={insight.insight_id}
                            className="p-4 border border-border rounded-lg"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <Badge variant="secondary">
                                {analyzeTypes.find(t => t.id === insight.analyze_type)?.name || insight.analyze_type}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {format(new Date(insight.created_at), "PPp", { locale: es })}
                              </span>
                            </div>
                            {insight.content_sample && (
                              <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                                "{insight.content_sample}"
                              </p>
                            )}
                            <p className="text-sm line-clamp-3">{insight.analysis}</p>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="mt-2"
                              onClick={() => {
                                setAnalysisResult({ analysis: insight.analysis });
                              }}
                            >
                              Ver completo
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}

          {/* Analysis Dialog */}
          <Dialog open={analyzeDialogOpen} onOpenChange={setAnalyzeDialogOpen}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Analizar Competidor</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Tipo de Análisis</Label>
                  <Select 
                    value={analysisRequest.analyze_type}
                    onValueChange={(v) => setAnalysisRequest({ ...analysisRequest, analyze_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {analyzeTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name} - {type.description}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Contenido a Analizar (opcional)</Label>
                  <Textarea 
                    placeholder="Pega el contenido de un post específico..."
                    value={analysisRequest.content_sample}
                    onChange={(e) => setAnalysisRequest({ ...analysisRequest, content_sample: e.target.value })}
                    className="min-h-[100px]"
                  />
                </div>
                <Button 
                  className="w-full" 
                  onClick={() => {
                    handleAnalyze();
                    setAnalyzeDialogOpen(false);
                  }}
                  disabled={analyzing}
                >
                  {analyzing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analizando...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Ejecutar Análisis
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </div>
  );
};
